import { Button, Col, Container, FormFeedback, FormGroup, FormText, Input, Label, ModalBody, ModalFooter, ModalHeader, Row, Badge } from "reactstrap";
import { useTheme } from "../../../hooks/useTheme";
import { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import CustomSelect from "../../../components/CustomSelect";
import DialogBox from "../../../components/DialogBox";
import { createImportTemplate, updateImportTemplate, importTemplateTest, replaceImportTemplateFile, getAWSSettings, getPreSignedUrl } from "../data/importTemplateService";
import { createImportTemplateTag, deleteImportTemplateTags } from "../data/importTemplateTagService";
import { downloadFileFromUrl } from '../../../utils/fileUtils';
import { createAudit, fetchAuditByFilter } from "../../audit/data/auditService";
import { getMapToSourceData } from "../data/mapToDataService";
import FileUpload from "../../../components/FileUpload";
import DataTable from "../../../components/DataTable";
import ConfigEditor from "./ConfigEditor";
import { useQuery } from "@tanstack/react-query";
import { fetchUsers } from "../../users/data/userService";
import { formatDateTime } from "../../../utils/dateUtils";
import { requiredExcelFileExtensions } from "../../../utils/excelUtils";

export default function AdminImportTemplateModal({ toggle, template: initialTemplate, tagsData, onTemplateChange, handleDelete }) {
    const theme = useTheme();
    const [template, setTemplate] = useState(structuredClone(initialTemplate));
    const [originalTemplate, setOriginalTemplate] = useState(structuredClone(initialTemplate));
    const [isDialogBoxOpen, setIsDialogBoxOpen] = useState(false);
    const [isDeleteDialogBoxOpen, setIsDeleteDialogBoxOpen] = useState(false);
    const [currentActiveTab, setCurrentActiveTab] = useState('Main');
    const [exportToS3, setExportToS3] = useState(false);
    const [isReuploaded, setIsReuploaded] = useState(false);
    const [isSaveButtonVisible, setIsSaveButtonVisible] = useState(true);

    useEffect(() => {
        const fetchAwsSettings = async () => {
            try {
                const settings = await getAWSSettings();
                setExportToS3(settings.uploadFile);
            } catch (error) {
                console.error("Error loading AWS settings:", error);
                setExportToS3(false);
            }
        }

        fetchAwsSettings();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTemplate(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setTemplate(prev => ({
            ...prev,
            s3Key: e.target.files[0].name,
            file: e.target.files[0]
        }));
        setIsReuploaded(true);
    };

    const handleFileDownload = async (key) => {
        const url = await getPreSignedUrl(key);
        if (!url) return;

        if (url.errorMessage)
            toast.error("Download failed. " + url.errorMessage);
        else
            await downloadFileFromUrl(url.presignedUrl);
    };

    const handleTagChange = (selectedOptions) => {
        setTemplate({ ...template, tags: selectedOptions.map(tag => ({ id: tag.value, tagName: tag.label })) });
    };

    const handleConfigJsonChange = (newConfigJson) => {
        setTemplate(prev => ({ ...prev, configJson: newConfigJson }));
    };

    // ---- minimal, safe file checks ----
    const hasRealFile = (f) => !!(f && typeof f === "object" && "name" in f);

    const validateTemplate = () => {
        let blankFields = [];
        if (!template.name) blankFields.push("Name");
        if (!template.description) blankFields.push("Description");
        if (!template.configJson) blankFields.push("Config");
        if (blankFields.length > 0) {
            toast.warning(`Please fill in: ${blankFields.join(", ")}`);
            return false;
        }

        const hasFile = hasRealFile(template.file);

        // Only require a file for brand-new templates
        if (template.id === 0 && !hasFile) {
            toast.warning('Please select a valid file.');
            return false;
        }

        // Only validate extension when a real file exists
        if (hasFile) {
            const validFileExtensions = requiredExcelFileExtensions.split(",").map(ext => ext.trim().toLowerCase());
            const fileName = String(template.file.name || "").toLowerCase();
            if (!validFileExtensions.some(ext => fileName.endsWith(ext))) {
                const errorMessage = `Error: Invalid file type. Only ${requiredExcelFileExtensions} files are allowed.`
                toast.warning(errorMessage);
                return false;
            }
        }

        return true;
    };

    const saveChanges = async () => {
        if (JSON.stringify(template) === JSON.stringify(originalTemplate)) {
            toggle();
            return;
        }
        if (!validateTemplate()) return;

        setIsSaveButtonVisible(false);

        const isNewTemplate = template.id === 0;
        let templateToSave = { ...template };
        let auditChanges = [];

        const saveTemplateIsOK = await saveTemplate(templateToSave, isNewTemplate, auditChanges);
        if (!saveTemplateIsOK) {
            setIsSaveButtonVisible(true);
            return;
        }

        await saveTemplateTags(templateToSave, isNewTemplate, auditChanges);
        await saveAudit(templateToSave, isNewTemplate, auditChanges);

        setTemplate(templateToSave);
        setOriginalTemplate(templateToSave); //update the original template in memory to compare with future changes
        onTemplateChange(templateToSave, isNewTemplate);
        toggle();

        setIsSaveButtonVisible(true);
    };

    const saveTemplate = async (template, isNew, auditChanges) => {
        let cleanTemplate = { ...template };
        delete cleanTemplate.tags;
        let cleanOriginalTemplate = { ...originalTemplate };
        delete cleanOriginalTemplate.tags;

        if (JSON.stringify(cleanTemplate) !== JSON.stringify(cleanOriginalTemplate)) {
            if (isNew) {
                const formData = new FormData();
                formData.set("file", cleanTemplate.file);
                formData.set("name", cleanTemplate.name);
                formData.set("description", cleanTemplate.description);
                formData.set("configJson", JSON.stringify(cleanTemplate.configJson));
                formData.set("disabled", cleanTemplate.disabled);
                const response = await createImportTemplate(formData);
                if (response.newId) {
                    auditTemplateChanges(true, auditChanges);
                    template.id = response.newId;
                    template.createdAt = response.createdAt;
                } else {
                    toast.warning(`Error: ${response}`);
                    return false;
                }
            } else {                    // existing template
                auditTemplateChanges(false, auditChanges);

                if (isReuploaded) {
                    if (!hasRealFile(cleanTemplate.file)) {
                        toast.warning("Please choose a file to upload.");
                        return false;
                    }
                    // upload whenever a new file was selected, even if it shares the old file's name
                    const formData = new FormData();
                    formData.set("file", cleanTemplate.file);
                    const fileReplaced = await replaceImportTemplateFile(cleanTemplate.id, formData);

                    // endpoint doesn't echo back the stored key, so just confirm the upload succeeded
                    if (!fileReplaced) {
                        toast.warning("Failed to upload the replacement file. Please try again.");
                        return false;
                    }
                }

                if (process.env.REACT_APP_USELOCALDATA === "true") {
                    template.updatedAt = new Date().toISOString();
                }
                else {
                    delete cleanTemplate.s3Key;
                    const response = await updateImportTemplate(cleanTemplate.id, { ...cleanTemplate, configJson: JSON.stringify(cleanTemplate.configJson) });
                    template.updatedAt = response.updatedAt;
                }
            }
        } else if (JSON.stringify(template.tags) !== JSON.stringify(originalTemplate.tags)) {  // if the template has not changed but the tags have, update the main template 'UpdatedAt' column
            template.updatedAt = new Date().toISOString();
            await updateImportTemplate(template.id, template);
        }
        return true;
    };

    const saveTemplateTags = async (template, isNew, auditChanges) => {
        if (JSON.stringify(template.tags) !== JSON.stringify(originalTemplate.tags)) {
            await deleteImportTemplateTags(template.id);
            await Promise.all(
                template.tags.map(async (tag) =>
                    await createImportTemplateTag({ importTemplateId: template.id, tagId: tag.id })
                )
            );
            auditTagChanges(isNew, auditChanges);
        }
    };

    const auditTemplateChanges = (isNewTemplate, auditChanges) => {
        const auditFieldsToCompare = ["name", "description", "disabled", "s3Key", "configJson"]
        auditFieldsToCompare.forEach((field) => {
            const originalField = JSON.stringify(originalTemplate[field]);
            const currentField = JSON.stringify(template[field]);

            if (!isNewTemplate) {
                if (currentField !== originalField) {
                    auditChanges.push(`\"${field}\": {\"new\": ${currentField}, \"old\": ${originalField}}`);
                }
            } else {
                auditChanges.push(`\"${field}\": ${currentField}`);
            }
        })
    }

    const auditTagChanges = (isNewTemplate, auditChanges) => {
        const field = "tags"
        const originalField = JSON.stringify(originalTemplate[field]);
        const currentField = JSON.stringify(template[field]);

        if (!isNewTemplate) {
            if (currentField !== originalField) {
                auditChanges.push(`\"${field}\": {\"new\": ${currentField}, \"old\": ${originalField}}`);
            }
        } else {
            auditChanges.push(`\"${field}\": ${JSON.stringify(template[field])}`);
        }
    }

    const saveAudit = async (template, isNew, auditChanges) => {
        if (auditChanges.length === 0) return;
        const auditEntry = "{" + auditChanges.join(", ") + "}";
        const action = isNew ? "Import template created" : "Import template changed";
        await createAudit({ action, result: "Successful", source: `importTemplate.${template.id}`, payload: auditEntry });
    };

    const handleClose = () => {
        if (JSON.stringify(template) !== JSON.stringify(originalTemplate)) {
            setIsDialogBoxOpen(true);
        } else {
            toggle();
        }
    };

    const onFileUpload = async (fileData) => {
        const mapToHeaders = await getMapToSourceData();
        
        fileData.set("configJson", JSON.stringify(template.configJson));
        fileData.set("mapToHeaders", JSON.stringify(mapToHeaders));
        const jsonData = await importTemplateTest(fileData);

        if (jsonData.error !== "")
            return { error: jsonData.error };

        return jsonData.entries;
    }

    // Reupload: set file to null (not an empty string)
    const handleReupload = () => {
        setTemplate({
            ...template,
            s3Key: '',
            file: null
        });
        setIsReuploaded(true)
    }

    const cancelReupload = () => {
        setTemplate({
            ...template,
            s3Key: originalTemplate.s3Key,
            file: null
        });
        setIsReuploaded(false);
    }

    const historyColumns = [
        {
            key: "timestamp",
            label: "Timestamp"
        },
        { key: "user", label: "User" },
        { key: "action", label: "Action" },
        { key: "notes", label: "Notes" },
        { key: "payload", label: "Payload" }
    ];

    const getUsersLookup = async () => {
        const users = await fetchUsers();
        return users.map(user => ({
            ...user,
            fullName: `${user.firstName} ${user.lastName}`
        }))
    }

    const {
        data: usersLookup,
        isLoading: isLoadingUsersLookup,
        error: usersLookupError,
    } = useQuery({
        queryKey: ["importTemplateUsers"],
        queryFn: getUsersLookup,
        staleTime: 60000
    });

    const getAudit = async () => {
        const filter = { "pageNumber": 0, "pageSize": 0, "source": `importTemplate.${template.id}` }
        const audits = await fetchAuditByFilter(filter);

        return audits.auditItems.map((audit) => {
            const user = usersLookup.find((user) => user?.userGuid === audit.userGuid);
            return {
                ...audit,
                timestamp: formatDateTime(audit.timestamp),
                user: user ? `${user.firstName} ${user.lastName}` : "Unknown",
            };
        });
    }

    const {
        data: auditData,
        isLoading: isLoadingAudits,
        error: auditError,
        refetch: refetchAudits,
    } = useQuery({
        queryKey: ["importTemplateAudits"],
        queryFn: getAudit,
        enabled: !!usersLookup, // Only run when usersLookup are available
        staleTime: 60000
    });

    useEffect(() => {
        if (usersLookup) {
            refetchAudits();
        }
    }, [usersLookup]);

    // Safe Save button disabled logic
    const saveDisabled =
        Array.isArray(template?.configJson?.sheets) &&
        template.configJson.sheets.some(s => (s?.stop?.text ?? "") === "");

    return (
        <>
            <ModalHeader toggle={handleClose}>
                {template.name || "Import Template Details"}
            </ModalHeader>

            <ModalBody>
                <Row className="modal-menu">
                    <Col style={{ display: 'inline-flex' }}>
                        {["Main", "Sheet Editor", "Template Tester"].map((tab) => (
                            <h6
                                key={tab}
                                className={currentActiveTab === tab ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setCurrentActiveTab(tab)}
                            >
                                {tab}
                            </h6>
                        ))}
                    </Col>
                    <Col className="text-end">
                        <h6
                            className={currentActiveTab === "History" ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                            style={{ cursor: 'pointer', display: 'inline-flex' }}
                            onClick={() => setCurrentActiveTab("History")}
                        >
                            History
                        </h6>
                    </Col>
                </Row>
                {currentActiveTab === 'Main' &&
                    <Container fluid>
                        <FormGroup row>
                            <Label for="name" sm={2}>Name*</Label>
                            <Col sm={10}>
                                <Input type="text" name="name" value={template.name || ""} onChange={handleChange} invalid={!template.name} />
                                <FormFeedback>Name is required.</FormFeedback>
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Label for="description" sm={2}>Description*</Label>
                            <Col sm={10}>
                                <Input type="textarea" name="description" value={template.description || ""} onChange={handleChange} invalid={!template.description} />
                                <FormFeedback>Description is required.</FormFeedback>
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Label for="tags" sm={2}>Tags</Label>
                            <Col sm={10}>
                                <CustomSelect
                                    isMulti
                                    creatable
                                    options={tagsData.map(tag => ({ value: tag.id, label: tag.tagName }))}
                                    onChange={handleTagChange}
                                    defaultValue={template.tags.map(tag => ({ value: tag.id, label: tag.tagName }))}
                                />
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Label for="disabled" sm={2}>Disabled</Label>
                            <Col sm={10}>
                                <Input type="checkbox" name="disabled" checked={template.disabled} onChange={handleChange} />
                            </Col>
                        </FormGroup>
                        <hr />
                        <FormGroup row style={{ alignItems: 'center' }}>
                            <Label for="file" sm={2}>Template File*</Label>
                            <Col sm={10}>
                                {template.id === 0 || !template.s3Key ? (
                                    <>
                                        <Input type="file" name="file" accept={requiredExcelFileExtensions} onChange={handleFileChange} invalid={!template.file} />
                                        <FormFeedback>File is required.</FormFeedback>
                                    </>
                                ) : (
                                    <>
                                        {isReuploaded &&
                                            <Badge style={{ marginRight: '8px' }}>NEW</Badge>
                                        }
                                        <FormText>{template.s3Key}</FormText>
                                        {!isReuploaded && (
                                            <Button style={{ marginLeft: '8px' }} size="sm" onClick={() => handleFileDownload(template.s3Key)}>Download</Button>
                                        )}
                                        {!isReuploaded &&
                                            <Button style={{ marginLeft: '8px' }} size="sm" onClick={() => handleReupload()}>Re-upload</Button>
                                        }
                                        {isReuploaded &&
                                            <Button color="danger" style={{ marginLeft: '8px' }} size="sm" onClick={cancelReupload}><i className="bi bi-trash"></i></Button>
                                        }
                                    </>
                                )}
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col sm={2}>
                            </Col>
                            <Col sm={10}>
                                {!exportToS3 && (<Label style={{ color: "#e29b28", fontSize: "0.85rem" }}>*configured not to save to S3</Label>)}
                            </Col>
                        </FormGroup>
                    </Container>
                }

                {currentActiveTab === 'Sheet Editor' &&
                    <ConfigEditor value={template.configJson || ""} onChange={handleConfigJsonChange} />
                }

                {currentActiveTab === 'Template Tester' &&
                    <FileUpload
                        isModal={false}
                        showFooterButtons={false}
                        requiredFileExtensions={requiredExcelFileExtensions}
                        onFileUpload={async (fileData) => onFileUpload(fileData)}
                    />
                }
                {currentActiveTab === "History" && (
                    <DataTable
                        columns={historyColumns}
                        data={auditData ?? []}
                        exportToCsv={true}
                        csvFileName="audit_items.csv"
                        sortByColumn="timestamp"
                        sortDirection="descending"
                        isLoading={isLoadingAudits || isLoadingUsersLookup}
                        error={auditError || usersLookupError}
                        searchableFields={["timestamp", "user", "action", "notes", "payload"]}
                    />
                )}
            </ModalBody>

            <ModalFooter style={{ justifyContent: 'space-between' }}>
                <div>
                    {template.id !== 0 && <Button color="danger" outline onClick={() => setIsDeleteDialogBoxOpen(true)}>Delete</Button>}
                </div>
                <div className="gap-2 d-flex">
                    <Button color={theme === "dark-mode" ? "dark" : "light"} outline onClick={handleClose}>Close</Button>
                    {isSaveButtonVisible &&
                        <Button disabled={saveDisabled} onClick={saveChanges}>Save</Button>
                    }
                </div>
            </ModalFooter>

            {/* Remove confirmation dialog */}
            <DialogBox
                isOpen={isDeleteDialogBoxOpen}
                title="Are you sure you want to delete?"
                message={
                    <div className="d-flex align-items-center">
                        <i className="bi bi-x-circle-fill text-danger fs-1 me-3"></i>
                        <span>Do you want to remove this import template?</span>
                    </div>
                }
                toggle={() => setIsDeleteDialogBoxOpen(false)}
                buttons={[
                    {
                        label: "No",
                        color: "secondary",
                        onClick: () => setIsDeleteDialogBoxOpen(false)
                    },
                    {
                        label: "Yes",
                        color: "primary",
                        onClick: () => handleDelete()
                    }
                ]}
            />

            <DialogBox
                isOpen={isDialogBoxOpen}
                title="Save Changes?"
                message={
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill text-warning fs-1 me-3"></i>
                        <span>You have unsaved changes.<br />Save changes before closing?</span>
                    </div>
                }
                toggle={() => setIsDialogBoxOpen(false)}
                buttons={[
                    { label: 'Yes', color: 'primary', onClick: () => { saveChanges(); setIsDialogBoxOpen(false); } },
                    { label: 'No', color: 'secondary', onClick: () => { setIsDialogBoxOpen(false); toggle(); } },
                    { label: 'Cancel', color: 'secondary', onClick: () => setIsDialogBoxOpen(false) }
                ]}
            />
        </>
    );
};
